import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Hook for background trace tasks with polling
 */
export const useTraceTask = () => {
  const [taskId, setTaskId] = useState(null);
  const [status, setStatus] = useState(null); // 'pending', 'running', 'completed', 'failed'
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Start a new trace task
  const startTrace = useCallback(async (txid, traceType, options = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/trace-tasks/`,
        {
          txid,
          trace_type: traceType,
          max_depth: options.maxDepth || 5,
          num_paths: options.numPaths || null,
          use_wallet_explorer: options.useWalletExplorer || false
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const task = response.data;
      setTaskId(task.id);
      setStatus(task.status);
      setProgress(task.progress);
      setCurrentStep(task.current_step);
      setResult(null);
      setError(null);
      setIsPolling(true);

      return task.id;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start trace');
      throw err;
    }
  }, [API_URL]);

  // Poll for task status
  useEffect(() => {
    if (!taskId || !isPolling) return;

    const pollInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/trace-tasks/${taskId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const task = response.data;
        setStatus(task.status);
        setProgress(task.progress);
        setCurrentStep(task.current_step);

        // Stop polling if task is complete or failed
        if (task.status === 'completed') {
          setResult(task.result);
          setIsPolling(false);
        } else if (task.status === 'failed') {
          setError(task.error_message);
          setIsPolling(false);
        }
      } catch (err) {
        console.error('Polling error:', err);
        setError(err.response?.data?.detail || 'Polling failed');
        setIsPolling(false);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [taskId, isPolling, API_URL]);

  // Cancel/reset
  const reset = useCallback(() => {
    setTaskId(null);
    setStatus(null);
    setProgress(0);
    setCurrentStep('');
    setResult(null);
    setError(null);
    setIsPolling(false);
  }, []);

  return {
    startTrace,
    reset,
    taskId,
    status,
    progress,
    currentStep,
    result,
    error,
    isPolling
  };
};
